import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Badge, Table, Button, Spinner, Alert, Tabs, Tab, ProgressBar } from 'react-bootstrap';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { FaServer, FaMemory, FaMicrochip, FaUsers, FaTerminal, FaSync, FaHdd, FaNetworkWired, FaClock, FaTachometerAlt, FaDatabase, FaCogs, FaCloudUploadAlt, FaPlug, FaCashRegister, FaShoppingCart, FaStore, FaCreditCard, FaExchangeAlt, FaBell, FaExclamationTriangle, FaExclamationCircle, FaInfoCircle } from 'react-icons/fa';
import MonitoringService from '../../../services/monitorService';
import { fetchIAAdminLogs } from '../../../services/api';

const OpsDashboard = () => {
    const [stats, setStats] = useState(null);
    const [usersData, setUsersData] = useState({ online_users: 0, users_list: [] });
    const [appStatus, setAppStatus] = useState(null);
    const [businessStatus, setBusinessStatus] = useState(null);
    const [alertsData, setAlertsData] = useState(null);
    const [logs, setLogs] = useState('');
    const [iaLogs, setIaLogs] = useState([]);
    const [lastUpdate, setLastUpdate] = useState(new Date());
    const [loading, setLoading] = useState(true);
    const logEndRef = useRef(null);

    // Fetch Metrics Loop
    useEffect(() => {
        const fetchMetrics = async () => {
            try {
                const [statsData, usersData] = await Promise.all([
                    MonitoringService.getSystemStats(),
                    MonitoringService.getActiveUsers()
                ]);

                if (statsData.success) {
                    // Processando dados do Netdata para formato do Recharts
                    // O controller já retorna os arrays de dados
                    setStats(formatChartData(statsData.data));
                }

                if (usersData.success) {
                    setUsersData(usersData);
                }
                setLastUpdate(new Date());
                setLoading(false);
            } catch (error) {
                console.error("Falha no monitoramento:", error);
            }
        };

        fetchMetrics();
        const interval = setInterval(fetchMetrics, 2000);
        return () => clearInterval(interval);
    }, []);

    // Fetch Logs Loop (Mais lento)
    useEffect(() => {
        const fetchLogs = async () => {
            try {
                const [res, iares] = await Promise.all([
                   MonitoringService.getLogs(100),
                   fetchIAAdminLogs()
                ]);
                if (res.success) setLogs(res.logs);
                if (iares && iares.success) setIaLogs(iares.logs);
            } catch (e) { console.error(e); }
        };

        fetchLogs();
        const interval = setInterval(fetchLogs, 5000);
        return () => clearInterval(interval);
    }, []);

    // Fetch App Status (PM2, bancos, backups, integrações) — a cada 10s
    useEffect(() => {
        const fetchApp = async () => {
            try {
                const r = await MonitoringService.getAppStatus();
                if (r?.success) setAppStatus(r.data);
            } catch (e) { console.error('app status:', e); }
        };
        fetchApp();
        const interval = setInterval(fetchApp, 10000);
        return () => clearInterval(interval);
    }, []);

    // Fetch Business Status — a cada 15s
    useEffect(() => {
        const fetchBusiness = async () => {
            try {
                const r = await MonitoringService.getBusinessStatus();
                if (r?.success) setBusinessStatus(r.data);
            } catch (e) { console.error('business status:', e); }
        };
        fetchBusiness();
        const interval = setInterval(fetchBusiness, 15000);
        return () => clearInterval(interval);
    }, []);

    // Fetch Alerts Timeline — a cada 20s
    useEffect(() => {
        const fetchAlerts = async () => {
            try {
                const r = await MonitoringService.getAlertsTimeline();
                if (r?.success) setAlertsData(r.data);
            } catch (e) { console.error('alerts:', e); }
        };
        fetchAlerts();
        const interval = setInterval(fetchAlerts, 20000);
        return () => clearInterval(interval);
    }, []);

    // Helper para formatar dados do Netdata
    const formatChartData = (rawData) => {
        if (!rawData || !rawData.cpu) return null;

        // Netdata retorna: { cpu: { data: [[time, val1, ...], ...] }, ... }
        // Pegamos os últimos 60 pontos e invertemos (mais recente por último)
        const historyLength = 60;

        const cpuPoints = rawData.cpu.data.slice(0, historyLength).reverse().map(p => ({
            time: new Date(p[0] * 1000).toLocaleTimeString(),
            value: p.slice(1).reduce((a, b) => a + Math.abs(b), 0).toFixed(1)
        }));

        const ramPoints = rawData.ram.data.slice(0, historyLength).reverse().map(p => {
            // RAM: [time, free, used, cached, buffers] - indices variam conforme OS, mas geralmente used é index 2
            // Netdata system.ram: usually [time, free, used, cached, buffers]
            const total = (p[1] + p[2] + p[3] + p[4]);
            const used = p[2];
            return {
                time: new Date(p[0] * 1000).toLocaleTimeString(),
                value: ((used / total) * 100).toFixed(1)
            };
        });

        // Network: latest point [time, received_kb, sent_kb] (kilobits/s by default)
        let netRx = 0, netTx = 0;
        const netPoints = [];
        if (rawData.net?.data?.length) {
            rawData.net.data.slice(0, historyLength).reverse().forEach((p) => {
                const rx = Math.abs(p[1] || 0);
                const tx = Math.abs(p[2] || 0);
                netPoints.push({ time: new Date(p[0] * 1000).toLocaleTimeString(), rx, tx });
            });
            const latest = rawData.net.data[0];
            netRx = Math.abs(latest[1] || 0);
            netTx = Math.abs(latest[2] || 0);
        }

        // Load: latest [time, load1, load5, load15]
        let load1 = 0, load5 = 0, load15 = 0;
        if (rawData.load?.data?.[0]) {
            const p = rawData.load.data[0];
            load1 = p[1] || 0;
            load5 = p[2] || 0;
            load15 = p[3] || 0;
        }

        // Uptime in seconds
        let uptimeSec = 0;
        if (rawData.uptime?.data?.[0]) {
            uptimeSec = rawData.uptime.data[0][1] || 0;
        }

        // Disks: { '/': { used_pct, free_gb, used_gb, total_gb }, ... }
        const disks = {};
        if (rawData.disks && typeof rawData.disks === 'object') {
            Object.entries(rawData.disks).forEach(([mount, d]) => {
                if (!d?.data?.[0]) return;
                const p = d.data[0];
                // Netdata disk_space: [time, avail, used, reserved_for_root]
                const avail = Math.abs(p[1] || 0);
                const used  = Math.abs(p[2] || 0);
                const reserved = Math.abs(p[3] || 0);
                const total = avail + used + reserved;
                disks[mount] = {
                    used_gb: used.toFixed(1),
                    free_gb: avail.toFixed(1),
                    total_gb: total.toFixed(1),
                    used_pct: total > 0 ? ((used / total) * 100).toFixed(1) : 0,
                };
            });
        }

        // Temperature (latest scalar)
        let tempC = null;
        if (rawData.temperature?.data?.[0]) {
            const p = rawData.temperature.data[0];
            const vals = p.slice(1).map((v) => Math.abs(v || 0)).filter((v) => v > 0);
            if (vals.length) tempC = (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
        }

        // Valores atuais (último ponto)
        return {
            charts: { cpu: cpuPoints, ram: ramPoints, net: netPoints },
            current: {
                cpu: cpuPoints[cpuPoints.length - 1]?.value || 0,
                ram: ramPoints[ramPoints.length - 1]?.value || 0,
                netRx, netTx,
                load1: load1.toFixed(2), load5: load5.toFixed(2), load15: load15.toFixed(2),
                uptimeSec,
                disks,
                tempC,
            }
        };
    };

    const fmtUptime = (secs) => {
        if (!secs) return '—';
        const d = Math.floor(secs / 86400);
        const h = Math.floor((secs % 86400) / 3600);
        const m = Math.floor((secs % 3600) / 60);
        if (d > 0) return `${d}d ${h}h ${m}m`;
        if (h > 0) return `${h}h ${m}m`;
        return `${m}m`;
    };
    const fmtNetSpeed = (kbps) => {
        if (kbps >= 1024) return (kbps / 1024).toFixed(2) + ' Mbps';
        return kbps.toFixed(1) + ' kbps';
    };
    const diskColor = (pct) => {
        const p = Number(pct);
        if (p >= 90) return 'danger';
        if (p >= 75) return 'warning';
        return 'success';
    };
    const fmtDateRel = (d) => {
        if (!d) return '—';
        const date = new Date(d);
        const diffMs = Date.now() - date.getTime();
        const min = Math.floor(diffMs / 60000);
        if (min < 1) return 'agora';
        if (min < 60) return `${min}m atrás`;
        const h = Math.floor(min / 60);
        if (h < 24) return `${h}h atrás`;
        return `${Math.floor(h / 24)}d atrás`;
    };
    const fmtSize = (mb) => {
        if (mb == null) return '—';
        if (mb >= 1024) return (mb / 1024).toFixed(1) + ' GB';
        return mb.toFixed(1) + ' MB';
    };
    const fmtBRL = (n) => 'R$ ' + (Number(n) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const pctDelta = (cur, prev) => {
        if (!prev || prev === 0) return cur > 0 ? '+∞' : '—';
        const p = ((cur - prev) / prev) * 100;
        const sign = p >= 0 ? '+' : '';
        return `${sign}${p.toFixed(1)}%`;
    };
    const statusBadge = (s) => {
        if (s === 'ok' || s === 'online') return <Badge bg="success">{s}</Badge>;
        if (s === 'down' || s === 'errored' || s === 'stopped') return <Badge bg="danger">{s}</Badge>;
        if (s === 'unknown') return <Badge bg="secondary">{s}</Badge>;
        return <Badge bg="warning" text="dark">{s}</Badge>;
    };

    if (loading && !stats) return <div className="p-5 text-center"><Spinner animation="border" variant="primary" /></div>;

    return (
        <Container fluid className="p-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="mb-0 fw-bold text-dark"><FaServer className="me-2" /> Gringão Ops Center</h2>
                    <small className="text-muted">Monitoramento em Tempo Real &bull; Atualizado: {lastUpdate.toLocaleTimeString()}</small>
                </div>
                <Badge bg="success" className="p-2 px-3 rounded-pill">SISTEMA ONLINE</Badge>
            </div>

            {/* KPI Cards */}
            <Row className="mb-4 g-3">
                <Col md={4}>
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Body className="d-flex align-items-center justify-content-between">
                            <div>
                                <h6 className="text-muted text-uppercase fw-bold mb-1">CPU Load</h6>
                                <h2 className="mb-0 fw-bold text-warning">{stats?.current?.cpu}%</h2>
                            </div>
                            <FaMicrochip size={40} className="text-warning opacity-50" />
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Body className="d-flex align-items-center justify-content-between">
                            <div>
                                <h6 className="text-muted text-uppercase fw-bold mb-1">Memória RAM</h6>
                                <h2 className="mb-0 fw-bold text-danger">{stats?.current?.ram}%</h2>
                            </div>
                            <FaMemory size={40} className="text-danger opacity-50" />
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Body className="d-flex align-items-center justify-content-between">
                            <div>
                                <h6 className="text-muted text-uppercase fw-bold mb-1">Usuários Ativos</h6>
                                <h2 className="mb-0 fw-bold text-primary">{usersData.online_users}</h2>
                            </div>
                            <FaUsers size={40} className="text-primary opacity-50" />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Sistema — Load, Uptime, Rede */}
            <Row className="mb-4 g-3">
                <Col md={4}>
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Body className="d-flex align-items-center justify-content-between">
                            <div>
                                <h6 className="text-muted text-uppercase fw-bold mb-1">Load Average</h6>
                                <h4 className="mb-0 fw-bold text-info">{stats?.current?.load1}</h4>
                                <small className="text-muted">5m: {stats?.current?.load5} · 15m: {stats?.current?.load15}</small>
                            </div>
                            <FaTachometerAlt size={36} className="text-info opacity-50" />
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Body className="d-flex align-items-center justify-content-between">
                            <div>
                                <h6 className="text-muted text-uppercase fw-bold mb-1">Uptime</h6>
                                <h4 className="mb-0 fw-bold text-success">{fmtUptime(stats?.current?.uptimeSec)}</h4>
                                <small className="text-muted">desde reboot</small>
                            </div>
                            <FaClock size={36} className="text-success opacity-50" />
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Body className="d-flex align-items-center justify-content-between">
                            <div>
                                <h6 className="text-muted text-uppercase fw-bold mb-1">Rede (RX/TX)</h6>
                                <h6 className="mb-0 fw-bold text-primary">↓ {fmtNetSpeed(stats?.current?.netRx || 0)}</h6>
                                <h6 className="mb-0 fw-bold text-warning">↑ {fmtNetSpeed(stats?.current?.netTx || 0)}</h6>
                            </div>
                            <FaNetworkWired size={36} className="text-primary opacity-50" />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Aplicação — PM2 + Bancos + Integrações */}
            <Row className="mb-4 g-3">
                <Col lg={6}>
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Header className="bg-white fw-bold py-3 border-bottom-0">
                            <FaCogs className="me-2 text-primary" /> Processos PM2
                        </Card.Header>
                        <Card.Body className="p-0">
                            {appStatus?.pm2?.length > 0 ? (
                                <Table hover responsive size="sm" className="mb-0 align-middle">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-3">Processo</th>
                                            <th>Status</th>
                                            <th className="text-end">CPU</th>
                                            <th className="text-end">RAM</th>
                                            <th className="text-end">Restarts</th>
                                            <th className="text-end pe-3">Uptime</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {appStatus.pm2.map((p) => (
                                            <tr key={p.name}>
                                                <td className="ps-3 fw-bold">{p.name}</td>
                                                <td>{statusBadge(p.status)}</td>
                                                <td className="text-end">{p.cpu}%</td>
                                                <td className="text-end">{p.mem_mb} MB</td>
                                                <td className="text-end">
                                                    <span className={p.restarts > 50 ? 'text-danger fw-bold' : ''}>
                                                        {p.restarts}
                                                    </span>
                                                </td>
                                                <td className="text-end pe-3">{fmtUptime(Math.floor(p.uptime_ms / 1000))}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <div className="p-4 text-center text-muted">Carregando PM2...</div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={6}>
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Header className="bg-white fw-bold py-3 border-bottom-0">
                            <FaDatabase className="me-2 text-info" /> Bancos de Dados
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table hover responsive size="sm" className="mb-0 align-middle">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="ps-3">Banco</th>
                                        <th>Status</th>
                                        <th className="text-end">Latência</th>
                                        <th className="text-end pe-3">Tamanho</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {appStatus?.databases?.mysql?.dashboard_app && (
                                        <tr>
                                            <td className="ps-3">
                                                <div className="fw-bold">MySQL dashboard_app</div>
                                                <small className="text-muted">local · {appStatus.databases.mysql.dashboard_app.tables || 0} tabelas</small>
                                            </td>
                                            <td>{statusBadge(appStatus.databases.mysql.dashboard_app.status)}</td>
                                            <td className="text-end">{appStatus.databases.mysql.dashboard_app.latency_ms}ms</td>
                                            <td className="text-end pe-3">{fmtSize(appStatus.databases.mysql.dashboard_app.size_mb)}</td>
                                        </tr>
                                    )}
                                    {appStatus?.databases?.mysql?.loja_fisica && (
                                        <tr>
                                            <td className="ps-3"><div className="fw-bold">MySQL loja_fisica</div><small className="text-muted">remoto</small></td>
                                            <td>{statusBadge(appStatus.databases.mysql.loja_fisica.status)}</td>
                                            <td className="text-end">{appStatus.databases.mysql.loja_fisica.latency_ms || '—'}ms</td>
                                            <td className="text-end pe-3">—</td>
                                        </tr>
                                    )}
                                    {appStatus?.databases?.redis && (
                                        <tr>
                                            <td className="ps-3"><div className="fw-bold">Redis</div><small className="text-muted">cache</small></td>
                                            <td>{statusBadge(appStatus.databases.redis.status)}</td>
                                            <td className="text-end">{appStatus.databases.redis.latency_ms ?? '—'}ms</td>
                                            <td className="text-end pe-3">—</td>
                                        </tr>
                                    )}
                                    {appStatus?.databases?.sqlite_caches?.slice(0, 5).map((c) => (
                                        <tr key={c.name}>
                                            <td className="ps-3">
                                                <div className="fw-bold">SQLite {c.name}</div>
                                                <small className="text-muted">cache local</small>
                                            </td>
                                            <td><Badge bg="success">ok</Badge></td>
                                            <td className="text-end">—</td>
                                            <td className="text-end pe-3">{fmtSize(c.size_mb)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Backups + Integrações */}
            <Row className="mb-4 g-3">
                <Col lg={8}>
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Header className="bg-white fw-bold py-3 border-bottom-0">
                            <FaCloudUploadAlt className="me-2 text-success" /> Backups
                            {appStatus?.backups?.total_size_mb && (
                                <small className="text-muted ms-2">total: {fmtSize(appStatus.backups.total_size_mb)}</small>
                            )}
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table hover responsive size="sm" className="mb-0 align-middle">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="ps-3">Tipo</th>
                                        <th>Último backup</th>
                                        <th>Quando</th>
                                        <th className="text-end">Tamanho atual</th>
                                        <th className="text-end">Arquivos</th>
                                        <th className="text-end pe-3">Total ocupado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[
                                        ['mysql',  '🗄️ MySQL dashboard_app', appStatus?.backups?.health?.mysql_ok],
                                        ['sqlite', '💾 SQLite caches',       appStatus?.backups?.health?.sqlite_ok],
                                        ['code',   '📦 Código',              appStatus?.backups?.health?.code_ok],
                                        ['env',    '🔑 .env (secrets)',      appStatus?.backups?.health?.env_ok],
                                    ].map(([key, label, ok]) => {
                                        const b = appStatus?.backups?.[key];
                                        return (
                                            <tr key={key}>
                                                <td className="ps-3 fw-bold">
                                                    {label}{' '}
                                                    {b?.last_at ? (ok
                                                        ? <Badge bg="success" pill className="ms-1">OK</Badge>
                                                        : <Badge bg="danger" pill className="ms-1">ATRASADO</Badge>
                                                    ) : <Badge bg="secondary" pill className="ms-1">—</Badge>}
                                                </td>
                                                <td><code style={{ fontSize: '0.75rem' }}>{b?.last_file || '—'}</code></td>
                                                <td>
                                                    <div>{b?.last_at ? new Date(b.last_at).toLocaleString('pt-BR') : '—'}</div>
                                                    <small className="text-muted">{fmtDateRel(b?.last_at)}</small>
                                                </td>
                                                <td className="text-end">{fmtSize(b?.last_size_mb)}</td>
                                                <td className="text-end">{b?.count ?? '—'}</td>
                                                <td className="text-end pe-3">{fmtSize(b?.total_size_mb)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                            {appStatus?.backups?.next_run_at && (
                                <div className="px-3 py-2 bg-light border-top">
                                    <small className="text-muted">
                                        <FaSync className="me-1" /> Próximo backup automático: <strong>{new Date(appStatus.backups.next_run_at).toLocaleString('pt-BR')}</strong> · cron 0 */6 * * *
                                    </small>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={4}>
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Header className="bg-white fw-bold py-3 border-bottom-0">
                            <FaPlug className="me-2 text-warning" /> Integrações
                        </Card.Header>
                        <Card.Body className="p-0">
                            {appStatus?.integrations?.length > 0 ? (
                                <Table hover responsive size="sm" className="mb-0 align-middle">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-3">Serviço</th>
                                            <th>Status</th>
                                            <th className="text-end pe-3">Latência</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {appStatus.integrations.map((i) => (
                                            <tr key={i.name}>
                                                <td className="ps-3 fw-bold text-capitalize">{i.name}</td>
                                                <td>{statusBadge(i.status)}</td>
                                                <td className="text-end pe-3">{i.latency_ms}ms</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <div className="p-4 text-center text-muted">—</div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Disco — Card por mount */}
            <Row className="mb-4 g-3">
                <Col>
                    <Card className="shadow-sm border-0">
                        <Card.Body>
                            <h6 className="fw-bold mb-3"><FaHdd className="me-2 text-secondary" /> Espaço em Disco</h6>
                            {stats?.current?.disks && Object.keys(stats.current.disks).length > 0 ? (
                                <Row className="g-3">
                                    {Object.entries(stats.current.disks).map(([mount, d]) => (
                                        <Col md={6} lg={4} key={mount}>
                                            <div className="border rounded p-3 h-100">
                                                <div className="d-flex justify-content-between mb-2">
                                                    <strong className="text-dark">{mount}</strong>
                                                    <span className={`fw-bold text-${diskColor(d.used_pct)}`}>{d.used_pct}%</span>
                                                </div>
                                                <ProgressBar
                                                    now={Number(d.used_pct)}
                                                    variant={diskColor(d.used_pct)}
                                                    style={{ height: 10 }}
                                                />
                                                <small className="text-muted d-block mt-2">
                                                    {d.used_gb} GB usados / {d.total_gb} GB · livre {d.free_gb} GB
                                                </small>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            ) : (
                                <div className="text-muted text-center py-3">Sem dados de disco do Netdata.</div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Caixas abertos + Syncs */}
            <Row className="mb-4 g-3">
                <Col lg={6}>
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Header className="bg-white fw-bold py-3 border-bottom-0">
                            <FaStore className="me-2 text-warning" /> Caixas abertos agora
                        </Card.Header>
                        <Card.Body className="p-0">
                            {businessStatus?.pdv?.caixas_abertos?.length > 0 ? (
                                <Table hover responsive size="sm" className="mb-0 align-middle">
                                    <thead className="bg-light">
                                        <tr>
                                            <th className="ps-3">Loja/Caixa</th>
                                            <th>Aberto há</th>
                                            <th className="text-end">Vendas</th>
                                            <th className="text-end pe-3">Total vendido</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {businessStatus.pdv.caixas_abertos.map((c) => (
                                            <tr key={c.id}>
                                                <td className="ps-3">
                                                    <div className="fw-bold">{c.loja}/{c.caixa}</div>
                                                    <small className="text-muted">Abertura: {fmtBRL(c.valor_abertura)}</small>
                                                </td>
                                                <td>
                                                    <div>{Math.floor(c.minutos_aberto / 60)}h {c.minutos_aberto % 60}m</div>
                                                    <small className="text-muted">{new Date(c.abertura_dh).toLocaleTimeString('pt-BR')}</small>
                                                </td>
                                                <td className="text-end">{c.vendas}</td>
                                                <td className="text-end pe-3 fw-bold text-success">{fmtBRL(c.total_vendido)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <div className="p-4 text-center text-muted">Nenhum caixa aberto agora.</div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={6}>
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Header className="bg-white fw-bold py-3 border-bottom-0">
                            <FaExchangeAlt className="me-2 text-info" /> Sincronizações recentes
                        </Card.Header>
                        <Card.Body className="p-0" style={{ maxHeight: 280, overflowY: 'auto' }}>
                            {businessStatus?.syncs?.length > 0 ? (
                                <Table hover responsive size="sm" className="mb-0 align-middle">
                                    <thead className="bg-light" style={{ position: 'sticky', top: 0 }}>
                                        <tr>
                                            <th className="ps-3">Serviço</th>
                                            <th>Quando</th>
                                            <th className="text-end pe-3">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {businessStatus.syncs.map((s, idx) => (
                                            <tr key={`${s.name}-${idx}`}>
                                                <td className="ps-3">
                                                    <div className="fw-bold text-capitalize" style={{ fontSize: '0.85rem' }}>{s.name}</div>
                                                    {s.info && <small className="text-muted">{s.info}</small>}
                                                </td>
                                                <td>
                                                    <div style={{ fontSize: '0.8rem' }}>{s.last_run ? new Date(s.last_run).toLocaleString('pt-BR') : '—'}</div>
                                                    <small className="text-muted">{fmtDateRel(s.last_run)}</small>
                                                </td>
                                                <td className="text-end pe-3">{statusBadge(s.status)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <div className="p-4 text-center text-muted">Sem dados de sincronização.</div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* ALERTAS — Timeline */}
            <Row className="mb-4 g-3">
                <Col>
                    <Card className="shadow-sm border-0">
                        <Card.Header className="bg-white fw-bold py-3 border-bottom d-flex justify-content-between align-items-center">
                            <div>
                                <FaBell className="me-2 text-danger" /> Timeline de Alertas
                                {alertsData?.summary && (
                                    <small className="text-muted ms-2">
                                        {alertsData.summary.total} eventos
                                    </small>
                                )}
                            </div>
                            {alertsData?.summary && (
                                <div className="d-flex gap-2">
                                    {alertsData.summary.critical > 0 && (
                                        <Badge bg="danger" className="px-2 py-1">
                                            <FaExclamationCircle className="me-1" />
                                            {alertsData.summary.critical} críticos
                                        </Badge>
                                    )}
                                    {alertsData.summary.warning > 0 && (
                                        <Badge bg="warning" text="dark" className="px-2 py-1">
                                            <FaExclamationTriangle className="me-1" />
                                            {alertsData.summary.warning} avisos
                                        </Badge>
                                    )}
                                    {alertsData.summary.info > 0 && (
                                        <Badge bg="info" className="px-2 py-1">
                                            <FaInfoCircle className="me-1" />
                                            {alertsData.summary.info} info
                                        </Badge>
                                    )}
                                    {alertsData.summary.total === 0 && (
                                        <Badge bg="success" className="px-2 py-1">✓ Tudo limpo</Badge>
                                    )}
                                </div>
                            )}
                        </Card.Header>
                        <Card.Body className="p-0" style={{ maxHeight: 420, overflowY: 'auto' }}>
                            {!alertsData ? (
                                <div className="p-4 text-center text-muted">Carregando alertas...</div>
                            ) : alertsData.alerts.length === 0 ? (
                                <div className="p-5 text-center">
                                    <h4 className="text-success mb-2">🎉 Tudo certo!</h4>
                                    <p className="text-muted mb-0">Nenhum alerta ativo no momento.</p>
                                </div>
                            ) : (
                                <div className="position-relative" style={{ padding: '12px 16px 0 16px' }}>
                                    {alertsData.alerts.map((a, idx) => {
                                        const color = a.level === 'critical' ? '#dc3545'
                                                    : a.level === 'warning'  ? '#f59e0b'
                                                    : '#0dcaf0';
                                        const Icon = a.level === 'critical' ? FaExclamationCircle
                                                   : a.level === 'warning'  ? FaExclamationTriangle
                                                   : FaInfoCircle;
                                        return (
                                            <div key={idx} className="d-flex pb-3 position-relative" style={{ marginLeft: 8 }}>
                                                {idx < alertsData.alerts.length - 1 && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        left: 11,
                                                        top: 28,
                                                        bottom: -4,
                                                        width: 2,
                                                        background: '#e5e7eb',
                                                    }} />
                                                )}
                                                <div style={{
                                                    width: 24, height: 24, borderRadius: '50%',
                                                    background: color, color: '#fff',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: 12, flexShrink: 0, zIndex: 1,
                                                }}>
                                                    <Icon />
                                                </div>
                                                <div className="ms-3 flex-grow-1">
                                                    <div className="d-flex justify-content-between align-items-start">
                                                        <div>
                                                            <Badge bg={a.level === 'critical' ? 'danger' : a.level === 'warning' ? 'warning' : 'info'}
                                                                   text={a.level === 'warning' ? 'dark' : 'white'}
                                                                   className="me-2 text-uppercase" style={{ fontSize: '0.7rem' }}>
                                                                {a.level}
                                                            </Badge>
                                                            <strong className="text-dark" style={{ fontSize: '0.85rem' }}>{a.source}</strong>
                                                        </div>
                                                        <small className="text-muted text-end">
                                                            <div>{fmtDateRel(a.when)}</div>
                                                            <div style={{ fontSize: '0.7rem' }}>{new Date(a.when).toLocaleString('pt-BR')}</div>
                                                        </small>
                                                    </div>
                                                    <div className="mt-1" style={{ fontSize: '0.85rem', color: '#475569', wordBreak: 'break-word' }}>
                                                        {a.message}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mb-4 g-3">
                {/* Graphics */}
                <Col lg={8}>
                    <Card className="shadow-sm border-0 mb-3">
                        <Card.Body>
                            <h6 className="fw-bold mb-3">Histórico de CPU</h6>
                            <div style={{ height: 250 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={stats?.charts?.cpu}>
                                        <defs>
                                            <linearGradient id="colorCpu" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#ffc107" stopOpacity={0.8} />
                                                <stop offset="95%" stopColor="#ffc107" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <XAxis dataKey="time" hide />
                                        <YAxis domain={[0, 100]} hide />
                                        <Tooltip />
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                                        <Area type="monotone" dataKey="value" stroke="#ffc107" fillOpacity={1} fill="url(#colorCpu)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </Card.Body>
                    </Card>

                    <Card className="shadow-sm border-0 mb-3">
                        <Card.Body>
                            <h6 className="fw-bold mb-3">Histórico de RAM</h6>
                            <div style={{ height: 250 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={stats?.charts?.ram}>
                                        <defs>
                                            <linearGradient id="colorRam" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#dc3545" stopOpacity={0.8} />
                                                <stop offset="95%" stopColor="#dc3545" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <XAxis dataKey="time" hide />
                                        <YAxis domain={[0, 100]} hide />
                                        <Tooltip />
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                                        <Area type="monotone" dataKey="value" stroke="#dc3545" fillOpacity={1} fill="url(#colorRam)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </Card.Body>
                    </Card>

                    <Card className="shadow-sm border-0">
                        <Card.Body>
                            <h6 className="fw-bold mb-3"><FaNetworkWired className="me-2 text-primary" /> Tráfego de Rede (kbps)</h6>
                            <div style={{ height: 200 }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={stats?.charts?.net}>
                                        <XAxis dataKey="time" hide />
                                        <YAxis hide />
                                        <Tooltip formatter={(v) => `${Number(v).toFixed(1)} kbps`} />
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                                        <Line type="monotone" dataKey="rx" stroke="#0d6efd" dot={false} name="Download" />
                                        <Line type="monotone" dataKey="tx" stroke="#ffc107" dot={false} name="Upload" />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Users & Lists */}
                <Col lg={4}>
                    <Card className="shadow-sm border-0 h-100">
                        <Card.Header className="bg-white fw-bold py-3 border-bottom-0">
                            <FaUsers className="me-2 text-primary" /> Usuários Logados
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table hover responsive className="mb-0 align-middle">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="border-0 ps-3">Usuário</th>
                                        <th className="border-0 text-end pe-3">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usersData.users_list.length > 0 ? (
                                        usersData.users_list.map(u => (
                                            <tr key={u.id}>
                                                <td className="ps-3">
                                                    <div className="fw-bold">{u.nome || u.email.split('@')[0]}</div>
                                                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>{u.email}</small>
                                                </td>
                                                <td className="text-end pe-3">
                                                    <Badge bg={u.status === 'online' ? 'success' : 'secondary'} pill>
                                                        {u.status}
                                                    </Badge>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr><td colSpan="2" className="text-center py-4 text-muted">Nenhum usuário ativo</td></tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Logs Terminal & Auditoria */}
            <Row>
                <Col>
                    <Card className="shadow-sm border-0">
                        <Card.Body className="p-0">
                            <Tabs defaultActiveKey="server" id="logs-tabs" className="mb-0 bg-dark rounded-top px-3 pt-2" variant="pills" style={{ '--bs-nav-link-color': '#adb5bd', '--bs-nav-pills-link-active-bg': '#198754', paddingBottom: '0.5rem' }}>
                                <Tab eventKey="server" title={<span className="fw-bold"><FaTerminal className="me-1" /> Infraestrutura do Servidor</span>}>
                                    <pre className="m-0 p-3 bg-dark text-light" style={{
                                        height: '400px', overflowY: 'auto', fontSize: '0.8rem', fontFamily: 'Consolas, monospace', color: '#10b981'
                                    }}>
                                        {logs || 'Carregando logs...'}
                                    </pre>
                                </Tab>
                                <Tab eventKey="ia" title={<span className="fw-bold"><FaMicrochip className="me-1" /> Auditoria I.A (Atividade do Chat)</span>}>
                                    <div className="p-0 bg-dark text-light" style={{ height: '400px', overflowY: 'auto' }}>
                                       <Table hover variant="dark" size="sm" className="mb-0 align-middle" style={{fontSize: '0.85rem'}}>
                                          <thead style={{ position: 'sticky', top: 0, zIndex: 1, backgroundColor: '#212529'}}>
                                             <tr>
                                                <th className="py-2 px-3">Data</th>
                                                <th className="py-2">Trancado na Loja</th>
                                                <th className="py-2">Usuário</th>
                                                <th className="py-2">Prompt Submetido</th>
                                                <th className="py-2">Resposta Simplificada</th>
                                             </tr>
                                          </thead>
                                          <tbody>
                                             {iaLogs?.length > 0 ? iaLogs.map(l => (
                                                <tr key={l.id}>
                                                   <td className="px-3" style={{whiteSpace:'nowrap', fontSize:'0.75rem', color: '#adb5bd'}}>{l.ts}</td>
                                                   <td><Badge bg={l.filtro_loja==='todas'?'primary':'warning'} text={l.filtro_loja!=='todas'?'dark':'white'} className="text-uppercase">{l.filtro_loja}</Badge></td>
                                                   <td><strong>{l.nome_usuario}</strong></td>
                                                   <td style={{maxWidth: '300px'}} className="text-truncate text-info">{l.pergunta}</td>
                                                   <td style={{maxWidth: '300px'}} className="text-truncate text-secondary">{l.resposta}</td>
                                                </tr>
                                             )) : <tr><td colSpan="5" className="text-center py-4 text-muted">Ainda não houve tráfego com a Inteligência Artificial no Tracker de Auditoria.</td></tr>}
                                          </tbody>
                                       </Table>
                                    </div>
                                </Tab>
                            </Tabs>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default OpsDashboard;
